import { z } from "zod";

/**
 * Execution node metadata - stored in WorkflowExecutionNode.metadata Json field.
 */
export const ExecutionNodeMetadataSchema = z.record(z.string(), z.unknown());

/**
 * The loop-specific keys the loop executor writes into
 * `WorkflowExecutionNode.metadata`, and the only ones the loop UI reads.
 *
 * A subset of {@link ExecutionNodeMetadataSchema} — unknown keys are stripped
 * rather than rejected, so a node carrying metadata from another concern still
 * parses. Parse with `safeParse` at the read boundary: the column is nullable
 * and holds whatever was written before this schema existed.
 */
export const LoopNodeMetadataSchema = z.object({
  /** Ids of the per-iteration WorkflowExecutions, in iteration order. */
  loopExecutionsOrderedIds: z.array(z.string()).optional(),
  /**
   * Total iterations the loop will run, known upfront for for-each loops. Absent
   * for do-while, where the total is not known until the loop stops.
   */
  loopTotalIterations: z.number().optional()
});

export type LoopNodeMetadata = z.infer<typeof LoopNodeMetadataSchema>;

/**
 * One failed attempt of a step that was then retried, as it stood when the
 * retry started.
 *
 * A retry runs IN the failed step's own row (`WorkflowExecutor`), and the reopen
 * resets the row's `createdAt` to the retry's start, so the row's `createdAt`,
 * `finishedAt`, `errorMessage` and `data` describe the LATEST attempt. Earlier
 * failures are kept under `metadata[RETRIED_ATTEMPTS_KEY]`, oldest first — the
 * FIRST failure always, then the most recent ones up to a bound the writer
 * states; `metadata[RETRIED_COUNT_KEY]` counts every failure archived, so a
 * list shorter than that count is a list with its middle dropped.
 */
export const RetriedAttemptSchema = z.object({
  /** The row's `retryCount` while this attempt ran — 0 for the first. */
  retryCount: z.number().int().nonnegative(),
  /** Bounded by the writer, and marked where the bound cut it. */
  errorMessage: z.string().nullable(),
  /** ISO-8601: when this attempt started — the row's `createdAt` while it ran. */
  startedAt: z.string().nullable(),
  /** ISO-8601; null where the attempt never recorded an end. */
  finishedAt: z.string().nullable(),
  /** The attempt's `data`, present only where it wrote any. */
  data: z.unknown().optional(),
  /** The row's `metadata`, present only where it was not a record and so could not be kept beside this list. */
  metadata: z.unknown().optional()
});

export type RetriedAttempt = z.infer<typeof RetriedAttemptSchema>;

/** The `WorkflowExecutionNode.metadata` key holding a retried step's earlier attempts. */
export const RETRIED_ATTEMPTS_KEY = "retriedAttempts";

/** The `WorkflowExecutionNode.metadata` key counting every failed attempt archived, kept or dropped. */
export const RETRIED_COUNT_KEY = "retriedCount";
